import React, { Component } from "react";
import { connect } from "react-redux";
import ReactPlayer from "react-player";
import { VideoURLForm } from "./VideoURLForm";
import styled from "styled-components";
import { UserControls } from "./UserControls";
import { RoomControls } from "./RoomControls";
import { ControlToolbar } from "./ControlToolbar";
import { LockControls } from "./LockControls";
import { broadcastAnd, setRoom } from "../redux/actions";
import FixedClock from "../lib/FixedClock";
import { SEEK_FUDGE_SECS } from "../redux/settings";
import { selectCanSeeControls } from "../redux/selectors.js";
import { ProgressBar } from "./ProgressBar";
import { LoadWarning } from "./LoadWarning";

const SmarterReactPlayerContainer = styled.div`
    background-color:black;
    height: 100%;
    display: flex;
    flex-direction:column;
`;

class SmarterReactPlayer extends Component {

    constructor(props) {
        super(props);
        this.player = React.createRef();
        this.state = { 
            playerReady: false, 
            showPlayer: true,
            showControls: true, 
            lastSawMouseMovement: Date.now(),
            needToRenderLoadWarning: false, 
            percent_loaded: 0,
            percent_played: 0,
        };
        this.interval = setInterval(this.heartbeat.bind(this), 1000);
    }

    componentWillUnmount() {
        clearInterval(this.interval);
    }

    heartbeat() {
        if (Date.now() - this.state.lastSawMouseMovement > 3000) {
            this.setState({ showControls: false });
        }
    }

    /* If the player is ready, and our calculated ideal position (calculated in PageRoom) is too far from the player, jump the player */
    // eslint-disable-next-line no-unused-vars
    componentDidUpdate(prevProps, prevState) {

        if (!this.state.playerReady || !this.state.showPlayer) {
            return;
        }

        if (prevProps.video_url !== this.props.video_url) {
            this.setState({ playerReady: false });
            return;
        }

        /* Hackaround to force reload since the reactplayer doesn't respond to a config change otherwise. FIXME */
        if (prevProps.subtitles_enabled !== this.props.subtitles_enabled) {
            this.setState({ showPlayer: false });
            setTimeout(()=> { this.setState({ showPlayer: true });},50);
        }

        /* If for any reason ideal_position is not getting calculated (--> undefined or NaN), treat as 0 so we'll jump */
        const safeIdealPosition = this.props.ideal_position ? this.props.ideal_position : 0;

        if (Math.abs(safeIdealPosition - this.player.current.getCurrentTime()) > SEEK_FUDGE_SECS) {
            this.player.current.seekTo(this.props.ideal_position);
        }
    }

    handleReady() {
        this.setState({ playerReady: true });
    }

    getFullRoomDetails = () => ({
        is_playing: this.props.is_playing,
        video_url: this.props.video_url,
        subtitle_url: this.props.subtitle_url,
        video_position: (this.props.ideal_position || 0),
        video_position_timestamp: FixedClock.utc(),
        video_length: this.props.video_length,
        password: this.props.password
    })

    getUserInfo = () => ({
        nick_name: this.props.nick_name,
        room_key: this.props.room_key
    });

    renderForm() {
        if (this.props.video_url === "") {
            return (
                <VideoURLForm />
            );
        }
    }

    /* Update duration so progress bar works, and tell the others if we're first */
    handleDuration(durationSecs) {
        if (!this.props.video_length) {
            this.props.broadcastAnd({
                ...this.getUserInfo(),
                namespace: "control",
                action: setRoom({ ...this.getFullRoomDetails(), video_length: durationSecs })
            });
        }
    }
    
    renderControlToolbar() {
        if (this.props.canSeeControls) {
            return (<ControlToolbar controls={<><UserControls/><LockControls/><RoomControls/></>} progressBar={<ProgressBar/>}/>);
        } else {
            return (<ControlToolbar controls={<><UserControls/><LockControls/></>} progressBar={null}/>);
        }
    }

    checkForVideoLoad(progress) {
        this.setState({
            needToRenderLoadWarning: (Math.floor(progress.played * 100) > Math.floor(progress.loaded * 100)),
            percent_loaded: (Math.round(progress.loaded * 100)),
            percent_played: (Math.round(progress.played * 100)),

        });
    }

    subtitleConfig() {
        return { 
                file: {
                    attributes: {
                        crossOrigin: "true"
                    },
                    tracks: [
                        { kind: "subtitles", src: this.props.subtitle_url, default: true }
                    ]
                } 
            };
    }

    render() {
        if (!this.state.showPlayer) { return (<div></div>); }
        return (
            <SmarterReactPlayerContainer onMouseMove={() => {this.setState({ lastSawMouseMovement: Date.now(), showControls: true });}}>
                <div style={{ flex: 4, flexGrow: 100, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>

                    {this.renderForm()}
                    {this.state.needToRenderLoadWarning? <LoadWarning percentGoal={ this.state.percent_played } percentLoaded = { this.state.percent_loaded }/> : null }

                    <ReactPlayer
                        onReady={this.handleReady.bind(this)}
                        onDuration={this.handleDuration.bind(this)}
                        onProgress={progress => this.checkForVideoLoad(progress)}
                        ref={this.player}
                        url={this.props.video_url}
                        playing={this.props.is_playing}
                        controls={false}
                        width='100%'
                        height='100%'
                        config = {this.props.subtitles_enabled ? this.subtitleConfig() : {}}
                    />
                </div>
                { (this.view_mode === "full" || this.state.showControls) ? this.renderControlToolbar() : null}

            </SmarterReactPlayerContainer>
        );
    }
}

const mapStateToProps = state => ({ 
    subtitles_enabled: state.user.subtitles_enabled,
    video_url: state.room.video_url,
    subtitle_url: state.room.subtitle_url,
    ideal_position: state.user.ideal_position,
    is_playing: state.room.is_playing,
    password: state.room.password,
    video_length: state.room.video_length,
    nick_name: state.user.nick_name,
    room_key: state.user.room_key,
    canSeeControls: selectCanSeeControls(state)
 });
export default connect(mapStateToProps, { broadcastAnd })(SmarterReactPlayer);